import { useState, useEffect } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { maakRuilverzoekApi } from '../lib/api.js';
import { datumNL } from '../lib/datum.js';
import DienstKiezer from './DienstKiezer.jsx';

export default function NieuwRuilverzoekModal({ open, onClose, onAangemaakt, eigenMwKey, collegas: collegasVanServer }) {
  const [stap, setStap] = useState(1);
  const [fout, setFout] = useState(null);
  const [gekozenDienst, setGekozenDienst] = useState(null);
  const [collegaKey, setCollegaKey] = useState(null);
  const [bezig, setBezig] = useState(false);

  // Reset bij openen. setState zit in een async fn (niet direct in de effect-body) i.v.m.
  // react-hooks/set-state-in-effect. De dienstenlijst zelf komt uit DienstKiezer (stap 1).
  useEffect(() => {
    if (!open) return;
    const reset = async () => {
      setStap(1);
      setGekozenDienst(null);
      setCollegaKey(null);
      setFout(null);
    };
    reset();
  }, [open]);

  // Escape sluit de modal.
  useEffect(() => {
    if (!open) return;
    const opKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', opKey);
    return () => window.removeEventListener('keydown', opKey);
  }, [open, onClose]);

  if (!open) return null;

  // Bron is de server (GET /api/ruilverzoeken, via Ruilverzoeken.jsx), bewust niet de store: de
  // store van een medewerker wordt nooit met live regels gevuld. Geen terugval — zonder prop een
  // lege lijst. Het eigenMwKey-filter is een dubbele bodem; sorteren gebeurt op een kopie.
  const collegas = (Array.isArray(collegasVanServer) ? [...collegasVanServer] : [])
    .filter((c) => c.key !== eigenMwKey)
    .sort((a, b) => (a.naam || '').localeCompare(b.naam || ''));

  const collega = collegas.find((c) => c.key === collegaKey) || null;

  const verstuur = async () => {
    if (!gekozenDienst || !collega || bezig) return;
    setFout(null);
    setBezig(true);
    try {
      // De server bepaalt de indiener uit het token; wij sturen alleen dienst + collega.
      await maakRuilverzoekApi({
        aangebodenDienst: gekozenDienst,
        collegaKey: collega.key,
        collegaNaam: collega.naam,
      });
      onAangemaakt?.();
      onClose();
    } catch (err) {
      setFout(err.message);
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800">
            {stap === 1 ? 'Welke dienst wil je ruilen?' : 'Met wie wil je ruilen?'}
          </h2>
          <button onClick={onClose} aria-label="Sluiten" className="p-1 rounded hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto space-y-3">
          {stap === 1 && (
            <DienstKiezer onKies={(d) => { setGekozenDienst(d); setStap(2); }} />
          )}

          {stap === 2 && (
            <>
              {gekozenDienst && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Jouw dienst</div>
                  <div className="text-sm font-medium text-slate-800 mt-0.5">{datumNL(gekozenDienst.datum)}</div>
                  <div className="text-xs text-slate-500">{gekozenDienst.dienstLabel} · {gekozenDienst.gemeenteLabel}</div>
                </div>
              )}

              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-1">Kies een collega</div>
              {collegas.length === 0 ? (
                <p className="text-sm text-slate-500">Er zijn geen collega&apos;s om mee te ruilen.</p>
              ) : (
                collegas.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setCollegaKey(c.key)}
                    className={`w-full text-left rounded-xl border p-3 transition-colors ${
                      collegaKey === c.key
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-slate-200 hover:border-brand-300 hover:bg-brand-50/40'
                    }`}
                  >
                    <span className="text-sm font-medium text-slate-800">{c.naam}</span>
                  </button>
                ))
              )}

              {fout && <p className="text-sm text-red-600 pt-1">{fout}</p>}
            </>
          )}
        </div>

        {/* Footer (alleen op stap 2) */}
        {stap === 2 && (
          <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
            <button
              onClick={() => setStap(1)}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4" /> Terug
            </button>
            <button
              onClick={verstuur}
              disabled={!gekozenDienst || !collega || bezig}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              {bezig ? 'Versturen…' : 'Ruilverzoek versturen'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
